import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Refresh as RefreshIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  onAddUser: (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    username: string;
    role: number;
  }) => Promise<{
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    username: string;
    role: number;
  }>;
}

const AddUserDialog: React.FC<AddUserDialogProps> = React.memo(({ open, onClose, onAddUser }) => {
  const { user: currentUser } = useAuth();
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    username: '',
    role: 2,
  });
  const [isPasswordGenerated, setIsPasswordGenerated] = useState(false);
  
  // Check if current user is root (role 0) - only root can assign admin role
  const isRoot = useMemo(() => {
    return currentUser?.role === 0;
  }, [currentUser?.role]);

  const generateStrongPassword = useCallback((): string => {
    const length = 16;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const allChars = uppercase + lowercase + numbers + symbols;
    
    let password = '';
    
    // Ensure at least one character from each category
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password to avoid predictable patterns
    return password.split('').sort(() => Math.random() - 0.5).join('');
  }, []);

  const handleGeneratePassword = useCallback(() => {
    const generatedPassword = generateStrongPassword();
    setNewUser(prev => ({ ...prev, password: generatedPassword }));
    setIsPasswordGenerated(true);
  }, [generateStrongPassword]);

  const handleCopyPassword = useCallback(async () => {
    if (newUser.password) {
      try {
        await navigator.clipboard.writeText(newUser.password);
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  }, [newUser.password]);

  const handleAddUser = useCallback(async () => {
    if (!newUser.email || !newUser.password || !newUser.firstName || !newUser.lastName || !newUser.username) {
      return;
    }

    await onAddUser(newUser);
    // Reset form after successful add
    setNewUser({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      username: '',
      role: 2,
    });
    setIsPasswordGenerated(false);
  }, [newUser, onAddUser]);

  // Memoize password field InputProps to prevent re-renders
  const passwordInputProps = useMemo(() => ({
    endAdornment: (
      <InputAdornment position="end">
        {isPasswordGenerated && newUser.password && (
          <IconButton
            onClick={handleCopyPassword}
            edge="end"
            title="Copy password"
            sx={{
              color: '#1976d2',
              mr: 0.5,
              '&:hover': {
                backgroundColor: '#f0f7ff',
              }
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          onClick={handleGeneratePassword}
          edge="end"
          title="Generate strong password"
          sx={{
            color: '#1976d2',
            '&:hover': {
              backgroundColor: '#f0f7ff',
            }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </InputAdornment>
    ),
  }), [handleGeneratePassword, handleCopyPassword, isPasswordGenerated, newUser.password]);

  // Reset form when dialog closes
  React.useEffect(() => {
    if (!open) {
      setNewUser({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        username: '',
        role: 2,
      });
      setIsPasswordGenerated(false);
    }
  }, [open]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ pb: 2, fontWeight: 600, fontSize: '1.5rem' }}>
        Add New User
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={newUser.firstName}
              onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={newUser.lastName}
              onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Username"
              value={newUser.username}
              onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Password"
              type={isPasswordGenerated ? "text" : "password"}
              value={newUser.password}
              onChange={(e) => {
                setNewUser(prev => ({ ...prev, password: e.target.value }));
                setIsPasswordGenerated(false);
              }}
              required
              InputProps={passwordInputProps}
              helperText={isPasswordGenerated ? "Password is visible. Click copy icon to copy it." : "Click the refresh icon to generate a strong password"}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newUser.role}
                onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value as number }))}
                label="Role"
              >
                <MenuItem value={2}>Viewer</MenuItem>
                <MenuItem value={1}>Editor</MenuItem>
                {isRoot && <MenuItem value={3}>Admin</MenuItem>}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button 
          onClick={onClose}
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 1.5,
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleAddUser} 
          variant="contained"
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 1.5,
            px: 3,
            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            }
          }}
        >
          Add User
        </Button>
      </DialogActions>
    </Dialog>
  );
});

AddUserDialog.displayName = 'AddUserDialog';

export default AddUserDialog;

